/** Posterize — per-channel quantization to N levels.
 *
 *  Each channel is bucketed into `levels` evenly-spaced output
 *  values: `levels=2` maps to [0, 255]; `levels=4` to [0, 85, 170,
 *  255]; etc. The mapping uses `floor(in * levels / 256)` to pick
 *  the bucket, then projects to the bucket's representative value
 *  with `bucket * 255 / (levels - 1)`.
 *
 *  Per-channel (not luminance) matches Photoshop's Posterize. Useful
 *  for poster / pop-art aesthetics and as a building block for
 *  channel-aware stylization downstream. */

export interface PosterizeValue {
  /** Output levels per channel (2..32). 2 produces a hard
   *  black-and-white-and-primaries look; 8 is typical "poster"
   *  feel; 32 is barely distinguishable from source. */
  levels: number;
}

// 32 is the value where successive posterized outputs become
// visually indistinguishable from the source on a typical 8-bit
// image (each bucket spans 8 source values, less than the visible
// quantization band). Treating 32 as identity keeps the slider's
// rightmost position visually equivalent to "no effect."
const POSTERIZE_IDENTITY_LEVELS = 32;

export const identityPosterizeValue = (): PosterizeValue => ({
  levels: POSTERIZE_IDENTITY_LEVELS,
});

export function applyPosterizePixels(source: ImageData, value: PosterizeValue): ImageData {
  const w = source.width;
  const h = source.height;
  const levels = Math.max(2, Math.min(32, Math.floor(value.levels)));
  if (levels === POSTERIZE_IDENTITY_LEVELS) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  // Pre-compute the lookup: for every 8-bit input value, what
  // output value does it map to? 256 entries × 1 byte = trivial
  // memory cost, replaces two multiplies + a divide per channel
  // per pixel with a single array lookup.
  const lut = new Uint8Array(256);
  const step = 255 / (levels - 1);
  for (let i = 0; i < 256; i++) {
    const bucket = Math.floor((i * levels) / 256);
    lut[i] = Math.round(bucket * step);
  }
  for (let i = 0; i < src.length; i += 4) {
    out[i] = lut[src[i]];
    out[i + 1] = lut[src[i + 1]];
    out[i + 2] = lut[src[i + 2]];
    out[i + 3] = src[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyPosterizeToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: PosterizeValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyPosterizePixels(source, value), 0, 0);
}
